import { Button } from '@atlas/ui-primitives'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateBrand, useCreateProduct } from '@/hooks/useCatalogWrites'
import { useClassificationBootstrap } from '@/hooks/useClassification'
import { useBrands } from '@/hooks/useExplore'
import { useCan } from '@/lib/capabilities'
import { notification } from '@/notifications/notification-store'
import { CatalogCreateDialog } from './CatalogCreateDialog'
import { CATALOG_AXIS } from './code-availability'

/**
 * The "add a brand / add a product" control, self-contained so the pages that host it do
 * not carry its state.
 *
 * The codes it checks against come from the bootstrap registry the session already holds —
 * see `code-availability` for why that is the right source rather than a new endpoint.
 *
 * Creating either one is ADMIN and above: a brand or product is an axis value the whole
 * tenant classifies against, so adding one edits everyone's vocabulary. The buttons stay
 * visible and are refused on press rather than hidden or disabled — a role that cannot do
 * this needs to learn who can, and a disabled button takes no pointer events to say it.
 */
const REFUSAL = 'Adding a brand or a product is an administrator task — ask an administrator to create it.'

export function AddCatalogEntry({ defaultBrandId }: { defaultBrandId?: string }) {
	const navigate = useNavigate()
	const { data: bootstrap } = useClassificationBootstrap()
	const { data: brands = [] } = useBrands()
	const createBrand = useCreateBrand()
	const createProduct = useCreateProduct()
	const [kind, setKind] = useState<'brand' | 'product' | null>(null)
	const { canConfigure } = useCan()

	const start = (next: 'brand' | 'product') => {
		if (!canConfigure) {
			notification.info(REFUSAL)
			return
		}
		setKind(next)
	}

	const existing = useMemo(() => {
		const axisId = bootstrap?.axes?.find((a) => a.code === CATALOG_AXIS)?.axisId
		if (!axisId) return []
		return (bootstrap?.valuesByAxis?.[axisId] ?? []).map((v) => ({ code: v.code, label: v.label }))
	}, [bootstrap])

	const submitting = createBrand.isPending || createProduct.isPending
	// Dimmed and cursor-marked, but still pressable: the press is what explains the refusal.
	const buttonClass = canConfigure ? 'rounded-ds-full' : 'rounded-ds-full cursor-not-allowed opacity-50'
	const why = canConfigure ? undefined : REFUSAL

	const create = (input: { code: string; name: string; brandId?: string }) => {
		if (kind === 'brand') {
			createBrand.mutate(
				{ code: input.code, name: input.name },
				{
					onSuccess: (brand) => {
						setKind(null)
						// Straight to the new brand: the next thing anyone does with a brand they
						// just made is fill it in.
						void navigate(`/explore/brand/${brand.brandId}`)
					},
				},
			)
			return
		}
		createProduct.mutate(
			{ code: input.code, name: input.name, brandId: input.brandId as string },
			{
				onSuccess: (product) => {
					setKind(null)
					void navigate(`/explore/product/${product.productId}`)
				},
			},
		)
	}

	return (
		<>
			<div className="flex gap-2">
				<Button type="button" variant="secondary" className={buttonClass} onPress={() => start('brand')}>
					<span title={why}>Add brand</span>
				</Button>
				<Button type="button" variant="secondary" className={buttonClass} onPress={() => start('product')}>
					<span title={why}>Add product</span>
				</Button>
			</div>
			<CatalogCreateDialog
				open={kind !== null}
				kind={kind ?? 'brand'}
				existing={existing}
				brands={brands.map((b) => ({ brandId: b.brandId, name: b.name }))}
				defaultBrandId={defaultBrandId}
				submitting={submitting}
				onCreate={create}
				onCancel={() => setKind(null)}
			/>
		</>
	)
}
